import type {
  OrderTicket,
  OrderTicketJob,
  PrintAgentRevokeReason,
  Printer,
  PrinterState,
  PrinterTransport,
  PrinterWidth,
  PrintingProblem,
  TicketKind,
} from '../types/printing.types';

/* ==========================================================================
   Textos da impressão

   A linguagem é a de quem trabalha na cozinha: "computador da loja",
   "impressora", "ponto de produção", "comanda", "conferência" e "via de
   entrega" — nunca agente, trabalho ou spooler.
   ========================================================================== */

export function formatTime(value: string | null | undefined): string {
  if (!value) return '';
  return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function formatDay(value: string | null | undefined): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/** "19:42" hoje, "26/09 19:42" em outro dia. */
export function formatMoment(value: string | null | undefined, now = new Date()): string {
  if (!value) return '';
  const date = new Date(value);
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay ? formatTime(value) : `${formatDay(value)} ${formatTime(value)}`;
}

/** "agora", "há 2 minutos", "há 1 hora". */
export function formatElapsed(value: string, now = Date.now()): string {
  const minutes = Math.max(0, Math.floor((now - new Date(value).getTime()) / 60_000));
  if (minutes < 1) return 'agora há pouco';
  if (minutes < 60) return `há ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
  const hours = Math.floor(minutes / 60);
  return `há ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
}

export const TRANSPORT_LABELS: Record<PrinterTransport, string> = {
  spooler: 'Direto',
  network: 'Rede',
  driver: 'Modo driver',
};

export const WIDTH_LABELS: Record<PrinterWidth, string> = {
  mm58: '58 mm',
  mm80: '80 mm',
  mm80_512: '80 mm (512 pontos)',
};

/** Ordem do seletor: o 80 mm comum primeiro, o de 512 pontos logo abaixo dele. */
export const WIDTH_OPTIONS: readonly PrinterWidth[] = ['mm80', 'mm80_512', 'mm58'];

/**
 * Ajuda do seletor de largura: quando escolher o 80 mm de 512 pontos, e quando
 * voltar dele (a cabeça de 576 imprime a página de 512 inteira, só mais estreita).
 */
export const WIDTH_HELP =
  'A maioria das térmicas de 80 mm imprime 576 pontos. Algumas, como a Epson TM-T88 e outras de 180 dpi, ' +
  'imprimem 512: se a régua da página de teste sair cortada à direita ou a página sair embaralhada, ' +
  'escolha 80 mm (512 pontos). Se com ela sobrar mais espaço à direita da régua do que à esquerda, volte para 80 mm.';

export const PRINTER_STATE_LABELS: Record<PrinterState, string> = {
  ok: 'Pronta',
  unreachable: 'Inacessível',
  missing: 'Ausente no computador',
  unknown: 'Sem sinal ainda',
};

/** Estado de uma impressora da loja, com o "sem computador" na frente de tudo. */
export function printerStateLabel(printer: Pick<Printer, 'available' | 'state'>): string {
  if (!printer.available) return 'Sem computador';
  return PRINTER_STATE_LABELS[printer.state];
}

export const REVOKE_REASON_LABELS: Record<PrintAgentRevokeReason, string> = {
  manual: 'revogado pelo dono ou gerente',
  credential_reuse: 'revogado por uso indevido da credencial',
  store_deactivated: 'revogado ao desativar a impressão',
};

const TICKET_KIND_LABELS: Record<TicketKind, string> = {
  production: 'Comanda',
  checkout: 'Conferência',
  delivery: 'Via de entrega',
};

/**
 * "da" ou "do" antes do nome do ponto: "da Chapa", "da Cozinha", "do Bar",
 * "do Forno". O nome é texto livre do dono; a terminação em "a" resolve a
 * maioria dos nomes de ponto em português sem pedir o gênero a ninguém.
 */
export function stationContraction(stationName: string): string {
  const last = stationName.trim().normalize('NFD').replace(/[^a-zA-Z]/g, '').slice(-1).toLowerCase();
  return last === 'a' ? 'da' : 'do';
}

/** "Comanda da Chapa", "Comanda do Bar", "Conferência", "Via de entrega". */
export function ticketLabel(kind: TicketKind, stationName?: string | null): string {
  if (kind === 'production') {
    return stationName ? `Comanda ${stationContraction(stationName)} ${stationName}` : 'Comanda';
  }
  return TICKET_KIND_LABELS[kind];
}

/** "a comanda da Chapa", "a conferência", "a via de entrega" — para o meio da frase. */
export function ticketPhrase(kind: TicketKind | null, stationName?: string | null): string {
  if (kind === 'production') {
    return stationName ? `a comanda ${stationContraction(stationName)} ${stationName}` : 'a comanda';
  }
  if (kind === 'checkout') return 'a conferência';
  if (kind === 'delivery') return 'a via de entrega';
  return 'o cupom';
}

/**
 * Estado de um trabalho no bloco "Impressão" do pedido. O texto é o `message`
 * da API, que é o mesmo do card e do detalhe e sabe o que o painel não sabe
 * (se o pedido foi cancelado, se o computador estava online no atraso). O
 * painel só acrescenta o horário no impresso e o "falhou:" na falha da
 * impressora.
 */
export function jobStateText(job: OrderTicketJob): string {
  switch (job.status) {
    case 'printed':
      return job.printedAt ? `enviado à impressora às ${formatTime(job.printedAt)}` : 'enviado à impressora';
    case 'failed':
      if (job.errorCode === 'printer_unreachable' || job.errorCode === 'printer_error') {
        return 'falhou: impressora inacessível';
      }
      if (job.errorCode === 'printer_unavailable') return 'sem impressora ativa para este cupom';
      if (job.errorCode === 'render_failed' || job.errorCode === 'dispatch_failed') {
        return 'falhou ao gerar o cupom';
      }
      return job.message || 'falhou';
    case 'pending':
    case 'delivered':
      // O atraso já vem classificado pela API: com o computador online, "não
      // confirmou em N s"; offline, "computador offline".
      return job.overdue ? job.message : 'na fila';
    case 'cancelled':
    case 'expired':
      // "cancelado" (pedido cancelado), "não impresso: impressão pausada" ou
      // "não impresso": só a API sabe se o pedido foi cancelado depois.
      if (job.message) return job.message;
      return job.status === 'cancelled' && job.cancelReason === 'printing_paused'
        ? 'não impresso: impressão pausada'
        : 'não impresso';
  }
}

/**
 * O `message` do cupom difere do do último trabalho: vale o do cupom. É o
 * pedido cancelado, em que todo cupom fora da fila é "cancelado", mesmo o que
 * já tinha saído.
 */
function ticketOverridesJob(ticket: OrderTicket): boolean {
  return !!ticket.lastJob && !!ticket.message && ticket.message !== ticket.lastJob.message;
}

/** Estado do cupom: o do último trabalho, a marca da API sem trabalho, ou o "cancelado" do pedido cancelado. */
export function ticketStateText(ticket: OrderTicket): string | null {
  if (ticket.lastJob && !ticketOverridesJob(ticket)) return jobStateText(ticket.lastJob);
  return ticket.message;
}

/** A falha que pede atenção: pinta o estado de vermelho. */
export function isTicketFailing(ticket: OrderTicket): boolean {
  const job = ticket.lastJob;
  if (!job || ticketOverridesJob(ticket)) return false;
  if (job.status === 'failed') return true;
  return (job.status === 'pending' || job.status === 'delivered') && job.overdue;
}

/** Texto da faixa para um problema, sem jargão técnico. */
export function problemText(problem: PrintingProblem): string {
  const name = problem.name ?? '';
  switch (problem.code) {
    case 'agent_offline':
      return problem.since
        ? `Computador da impressão offline desde ${formatMoment(problem.since)}: os pedidos não estão saindo no papel`
        : 'Computador da impressão offline: os pedidos não estão saindo no papel';
    case 'printer_unreachable':
      return `Impressora ${name} inacessível: os cupons dela não estão saindo`;
    case 'printer_missing':
      return `Impressora ${name} não foi encontrada no computador da loja: os cupons dela não estão saindo`;
    case 'printer_without_agent':
      return `Impressora ${name} está sem computador: os cupons dela não estão saindo`;
    case 'agent_revoked':
      return `Impressora ${name} é de um computador revogado: escolha outra impressora para os cupons dela`;
    case 'station_without_printer':
      return `A comanda do ponto ${name} está sem impressora ativa`;
  }
}
